/**
 * Global Zustand store.
 *
 * Holds personas, conversations, the active chat state (v1), plus
 * projects, documents, token usage, and toasts (§7g).
 *
 * Pages dispatch load actions; components subscribe to slices they
 * care about. Errors are surfaced per-slice as `<slice>Error` strings
 * so components can render them inline without prop drilling.
 */

import { create } from "zustand";
import * as api from "../services/api.js";
import * as projectsApi from "../services/projects.js";
import * as documentsApi from "../services/documents.js";
import { getTenantStatus } from "../services/tenant.js";
import * as jobsApi from "../services/jobs.js";
import * as usageApi from "../services/usage.js";

let nextToastId = 1;
const TOAST_TTL_MS = 4500;

const ACTIVE_TENANT_STORAGE_KEY = "aicoach.activeTenantId";

function loadPersistedActiveTenantId() {
  try {
    return window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function persistActiveTenantId(tenantId) {
  try {
    if (tenantId) {
      window.localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
    } else {
      window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    }
  } catch {
    // Private browsing / storage disabled — tolerated, header just won't
    // survive reload (same as pre-fix behaviour).
  }
}

/**
 * Convert persisted messages (which include role:"assistant" with toolCall
 * metadata and role:"tool" result entries) into the display format used by
 * the chat thread. Adjacent tool-call / tool-result pairs are collapsed
 * into a single { role: "tool_activity" } entry. Lifted from Chat.jsx
 * into the store as part of the G10 hydration-API alignment: hydration
 * now takes a full conversation record and the store owns the message
 * shape transform.
 */
function hydrateChatMessages(raw) {
  const out = [];
  let i = 0;
  while (i < raw.length) {
    const m = raw[i];
    if (m.role === "assistant" && m.toolCall) {
      const activities = [];
      while (i < raw.length) {
        const tc = raw[i];
        if (tc.role === "assistant" && tc.toolCall) {
          const activity = {
            toolUseId: tc.toolCall?.id || `tc_${i}`,
            tool: tc.toolCall?.name || "unknown_tool",
            input: tc.toolCall?.input || {},
            status: "done",
          };
          i++;
          if (i < raw.length && raw[i].role === "tool") {
            activity.summary = raw[i].content;
            i++;
          }
          activities.push(activity);
        } else {
          break;
        }
      }
      if (activities.length > 0) {
        out.push({ role: "tool_activity", activities });
      }
      continue;
    }
    if (m.role === "tool") {
      i++;
      continue;
    }
    out.push({ role: m.role, content: m.content });
    i++;
  }
  return out;
}

// Phase 43.4 — tenant-status stale window. After a successful fetch we
// don't re-fetch for this many ms on subsequent `loadTenantStatus` calls
// — the banner should update on navigation or focus, not on every render.
const TENANT_STATUS_STALE_MS = 60_000;

export const useStore = create((set, get) => ({
  // ── Tenant status (Phase 43.4) ───────────────────────────────────────────
  // Source of truth for the setup-incomplete banner. Keeps the last
  // successful fetch + a timestamp; `loadTenantStatus` is a no-op inside
  // the staleness window unless `force: true` is passed.
  tenantStatus: null,       // { tenantId, status, role }
  tenantStatusFetchedAt: 0, // epoch ms
  tenantStatusLoading: false,
  tenantStatusError: null,

  loadTenantStatus: async ({ force = false } = {}) => {
    const { tenantStatus, tenantStatusFetchedAt, tenantStatusLoading } = get();
    if (tenantStatusLoading) return;
    if (
      !force &&
      tenantStatus &&
      Date.now() - tenantStatusFetchedAt < TENANT_STATUS_STALE_MS
    ) {
      return;
    }
    set({ tenantStatusLoading: true, tenantStatusError: null });
    try {
      const data = await getTenantStatus();
      set({
        tenantStatus: data,
        tenantStatusFetchedAt: Date.now(),
        tenantStatusLoading: false,
      });
    } catch (err) {
      set({
        tenantStatusError: err.message,
        tenantStatusLoading: false,
      });
    }
  },

  clearTenantStatus: () =>
    set({
      tenantStatus: null,
      tenantStatusFetchedAt: 0,
      tenantStatusError: null,
    }),

  // ── Personas ─────────────────────────────────────────────────────────────
  personas: [],
  personasLoading: false,
  personasError: null,

  loadPersonas: async () => {
    if (get().personas.length > 0) return;
    set({ personasLoading: true, personasError: null });
    try {
      const personas = await api.listPersonas();
      set({ personas, personasLoading: false });
    } catch (err) {
      set({ personasError: err.message, personasLoading: false });
    }
  },

  getPersonaById: (id) => get().personas.find((p) => p.id === id),

  // ── Conversations ────────────────────────────────────────────────────────
  conversations: [],
  conversationsLoading: false,
  conversationsError: null,

  loadConversations: async (projectId = null) => {
    // Clear synchronously before the fetch so the stale list from a
    // previous project (or scope) doesn't render during the async gap.
    // The stale rows were a real data-integrity bug: ProjectConversationsTab
    // constructs Link hrefs from the URL's :projectId and each row's
    // c.id, so a stale row clicked mid-navigation produced an href
    // mashing project B with conversation A. Fix is to never show
    // stale state in the first place.
    set({
      conversations: [],
      conversationsLoading: true,
      conversationsError: null,
    });
    try {
      const conversations = await api.listConversations(projectId);
      set({ conversations, conversationsLoading: false });
    } catch (err) {
      set({
        conversationsLoading: false,
        conversationsError: err.message,
      });
    }
  },

  removeConversation: async (id) => {
    await api.deleteConversation(id);
    set({ conversations: get().conversations.filter((c) => c.id !== id) });
  },

  upsertConversation: (conv) => {
    const existing = get().conversations.filter((c) => c.id !== conv.id);
    set({ conversations: [conv, ...existing] });
  },

  // ── Active chat ──────────────────────────────────────────────────────────
  activeMessages: [],
  isStreaming: false,

  // Hydrate from a fetched conversation record (deep-links, sidebar nav).
  // Takes the full conversation (matches `hydrateActiveRoundtable` below)
  // and transforms `conversation.messages` into the display shape the
  // chat thread expects — tool_call / tool_result pairs collapsed into
  // `tool_activity` entries, bare "tool" roles dropped.
  hydrateActiveConversation: (conversation) => {
    const messages = hydrateChatMessages(conversation?.messages || []);
    set({ activeMessages: messages });
  },
  appendUserMessage: (content) =>
    set({
      activeMessages: [
        ...get().activeMessages,
        { role: "user", content },
      ],
    }),
  startAssistantMessage: () =>
    set({
      isStreaming: true,
      activeMessages: [
        ...get().activeMessages,
        { role: "assistant", content: "" },
      ],
    }),
  appendAssistantToken: (token) => {
    const messages = [...get().activeMessages];
    const last = messages[messages.length - 1];
    if (last && last.role === "assistant") {
      messages[messages.length - 1] = { ...last, content: last.content + token };
    } else {
      // Tokens arriving after a tool_activity block — start a new assistant message
      messages.push({ role: "assistant", content: token });
    }
    set({ activeMessages: messages });
  },

  // ── Agent tool activity (§8.8) ────────────────────────────────────────
  // During an agent turn, tool_call / tool_result SSE events arrive
  // between text token runs. We insert them as { role: "tool_activity" }
  // entries in activeMessages so they render inline in the chat thread.
  //
  // Each tool_activity message accumulates calls for a single tool-use
  // round. A new assistant message is started when tokens resume after
  // tool results.

  appendToolCall: ({ toolUseId, tool, input }) => {
    const messages = [...get().activeMessages];
    const last = messages[messages.length - 1];
    const activity = { toolUseId, tool, input, status: "running" };

    // If the last message is an empty assistant bubble (created by
    // startAssistantMessage before any tokens arrived), remove it so
    // the tool activity block renders cleanly without a blank bubble.
    if (last?.role === "assistant" && !last.content) {
      messages.pop();
    }

    const newLast = messages[messages.length - 1];
    if (newLast?.role === "tool_activity") {
      messages[messages.length - 1] = {
        ...newLast,
        activities: [...newLast.activities, activity],
      };
    } else {
      messages.push({ role: "tool_activity", activities: [activity] });
    }
    set({ activeMessages: messages });
  },

  resolveToolResult: ({ toolUseId, summary }) => {
    const messages = [...get().activeMessages];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role !== "tool_activity") continue;
      const idx = messages[i].activities.findIndex(
        (a) => a.toolUseId === toolUseId,
      );
      if (idx === -1) continue;
      const updated = [...messages[i].activities];
      updated[idx] = { ...updated[idx], status: "done", summary };
      messages[i] = { ...messages[i], activities: updated };
      set({ activeMessages: messages });
      return;
    }
    console.warn(
      `[tool_activity] resolveToolResult: no activity found for toolUseId=${toolUseId}; dropped`,
    );
  },

  finishStreaming: () => {
    // Sweep any still-running tool activities (missed tool_result events
    // or premature stream end) so stale spinners don't persist.
    const messages = get().activeMessages.map((m) => {
      if (m.role !== "tool_activity") return m;
      const hasRunning = m.activities.some((a) => a.status === "running");
      if (!hasRunning) return m;
      return {
        ...m,
        activities: m.activities.map((a) =>
          a.status === "running"
            ? { ...a, status: "done", summary: "completed" }
            : a,
        ),
      };
    });
    set({ isStreaming: false, activeMessages: messages });
    get().loadUsage();
  },
  clearActiveChat: () => set({ activeMessages: [], isStreaming: false }),

  // ── Projects (§7g) ───────────────────────────────────────────────────────
  projects: [],
  projectsLoading: false,
  projectsError: null,
  currentProject: null,

  loadProjects: async ({ includeShared = false } = {}) => {
    set({ projectsLoading: true, projectsError: null });
    try {
      const projects = await projectsApi.listProjects({ includeShared });
      set({ projects, projectsLoading: false });
    } catch (err) {
      set({ projectsError: err.message, projectsLoading: false });
    }
  },

  getProjectById: (id) => get().projects.find((p) => p.id === id),

  loadProject: async (id) => {
    // Clear the current project synchronously so a stale header (name
    // + description) from the previously-viewed project doesn't render
    // at the new project's URL during the async fetch. ProjectView's
    // skeleton will show until the cached or fetched project lands.
    set({ currentProject: null, projectsError: null });
    // Try the cached list first, then fall back to a single-project fetch.
    const cached = get().getProjectById(id);
    if (cached) {
      set({ currentProject: cached });
      return cached;
    }
    try {
      const project = await projectsApi.getProject(id);
      set({ currentProject: project });
      return project;
    } catch (err) {
      set({ projectsError: err.message, currentProject: null });
      throw err;
    }
  },

  createProject: async ({ name, description, companyUrl }) => {
    const project = await projectsApi.createProject({ name, description, companyUrl });
    set({ projects: [project, ...get().projects] });
    return project;
  },

  updateProject: async (id, patch) => {
    const updated = await projectsApi.updateProject(id, patch);
    set({
      projects: get().projects.map((p) => (p.id === id ? updated : p)),
      currentProject:
        get().currentProject?.id === id ? updated : get().currentProject,
    });
    return updated;
  },

  /** Optimistic sales stage update for kanban drag-and-drop. */
  updateProjectStage: (projectId, stage) => {
    set((state) => ({
      projects: state.projects.map((p) =>
        p.id === projectId ? { ...p, salesStage: stage } : p,
      ),
      currentProject:
        state.currentProject?.id === projectId
          ? { ...state.currentProject, salesStage: stage }
          : state.currentProject,
    }));
  },

  archiveProject: async (id) => {
    await projectsApi.archiveProject(id);
    set({
      projects: get().projects.filter((p) => p.id !== id),
      currentProject:
        get().currentProject?.id === id ? null : get().currentProject,
    });
  },

  // ── v4.1 Phase 36.9: project member management ─────────────────────────
  // Simple optimistic updates — the canonical project row carries
  // `members` as a list of `{userId, role, addedAt, addedBy}`. After a
  // mutation we refetch the project to pick up the server's normalised
  // shape (addedAt/updatedAt timestamps etc.).

  addProjectMember: async (projectId, payload) => {
    const result = await projectsApi.addProjectMember(projectId, payload);
    // Refetch the canonical project to sync members[].
    if (!result?.pending) {
      const updated = await projectsApi.getProject(projectId);
      set({
        projects: get().projects.map((p) =>
          p.id === projectId ? updated : p,
        ),
        currentProject:
          get().currentProject?.id === projectId ? updated : get().currentProject,
      });
    }
    return result;
  },

  removeProjectMember: async (projectId, userId) => {
    await projectsApi.removeProjectMember(projectId, userId);
    // Local update: drop the member.
    const dropMember = (project) => ({
      ...project,
      members: (project.members || []).filter((m) => m.userId !== userId),
    });
    set({
      projects: get().projects.map((p) =>
        p.id === projectId ? dropMember(p) : p,
      ),
      currentProject:
        get().currentProject?.id === projectId
          ? dropMember(get().currentProject)
          : get().currentProject,
    });
  },

  updateProjectMemberRole: async (projectId, userId, role) => {
    await projectsApi.updateProjectMember(projectId, userId, { role });
    const patchMember = (project) => ({
      ...project,
      members: (project.members || []).map((m) =>
        m.userId === userId ? { ...m, role } : m,
      ),
    });
    set({
      projects: get().projects.map((p) =>
        p.id === projectId ? patchMember(p) : p,
      ),
      currentProject:
        get().currentProject?.id === projectId
          ? patchMember(get().currentProject)
          : get().currentProject,
    });
  },

  transferProjectOwnership: async (projectId, newOwnerId) => {
    const result = await projectsApi.transferProjectOwnership(
      projectId,
      newOwnerId,
    );
    // Refetch — the row may have been re-keyed under a new PK so the
    // local shape after transfer is best rebuilt from authoritative data.
    const updated = await projectsApi.getProject(projectId);
    set({
      projects: get().projects.map((p) =>
        p.id === projectId ? updated : p,
      ),
      currentProject:
        get().currentProject?.id === projectId ? updated : get().currentProject,
    });
    return result;
  },

  revokeProjectInvite: async (projectId, email) => {
    await projectsApi.revokeProjectInvite(projectId, email);
  },

  clearCurrentProject: () => set({ currentProject: null }),

  /**
   * Clear every slice that belongs to "being inside a project": the
   * current project record, the documents list, and the conversations
   * list, along with their loading/error sub-slices.
   *
   * G9 (JAM-144): single action for project-scope teardown so that
   * `ProjectView`'s unmount cleanup, any future delete-project flow,
   * and the archive path don't drift from one another by forgetting a
   * slice or leaving behind a stale `documentsError`.
   *
   * Deliberately does NOT touch: personas (global), auth, active chat
   * messages, roundtable state, jobs list, toasts, or the `projects`
   * list itself (ProjectList needs that when navigating home).
   */
  resetProjectScope: () =>
    set({
      currentProject: null,
      documents: [],
      documentsLoading: false,
      documentsError: null,
      conversations: [],
      conversationsLoading: false,
      conversationsError: null,
    }),

  // ── Documents (§7g) ──────────────────────────────────────────────────────
  // Scoped to the current project; cleared when navigating away.
  documents: [],
  documentsLoading: false,
  documentsError: null,

  loadDocuments: async (projectId) => {
    // Synchronous clear to prevent stale documents from a previous
    // project leaking into the new project's view — same class of bug
    // as loadConversations' stale-row issue.
    set({ documents: [], documentsLoading: true, documentsError: null });
    try {
      const documents = await documentsApi.listDocuments(projectId);
      set({ documents, documentsLoading: false });
    } catch (err) {
      set({ documentsError: err.message, documentsLoading: false });
    }
  },

  uploadDocument: async (projectId, file) => {
    // Three-step pre-signed URL flow handled by documents.uploadFile.
    // On success, optimistically prepend the document to the list with
    // `status: "ready"` per §7.8 Q9 — no polling, no indexing state UI.
    await documentsApi.uploadFile(projectId, file);
    const optimistic = {
      filename: file.name,
      s3Key: `projects/${projectId}/${file.name}`,
      size: file.size,
      lastModified: new Date().toISOString(),
      status: "ready",
    };
    set({
      documents: [
        optimistic,
        ...get().documents.filter((d) => d.filename !== file.name),
      ],
    });
  },

  removeDocument: async (projectId, filename) => {
    await documentsApi.deleteDocument(projectId, filename);
    set({
      documents: get().documents.filter((d) => d.filename !== filename),
    });
  },

  clearDocuments: () => set({ documents: [] }),

  // ── Jobs (§13 — research pipeline lifecycle) ─────────────────────────────
  // The Jobs tab polls an in-flight job every 5s via pollJobStatus, which
  // returns the freshest record and also updates the `jobs` list so the
  // table reflects status transitions without an extra fetchJobs call.
  jobs: [],
  jobsLoading: false,
  jobsError: null,
  currentJob: null,
  // §15.3: agent IDs currently regenerating. The Documents tab shows an
  // in-row spinner for each entry. Stored as a plain array (not a Set) so
  // Zustand shallow-comparison treats updates correctly.
  regeneratingAgents: [],

  fetchJobs: async (projectId) => {
    set({ jobsLoading: true, jobsError: null });
    try {
      const jobs = await jobsApi.listJobs(projectId);
      set({ jobs, jobsLoading: false });
    } catch (err) {
      set({ jobsError: err.message, jobsLoading: false });
    }
  },

  loadJob: async (projectId, jobId) => {
    set({ currentJob: null });
    const job = await jobsApi.getJob(projectId, jobId);
    set({ currentJob: job });
    return job;
  },

  createJob: async (
    projectId,
    { mode = "full", priorJobId, agentId } = {},
  ) => {
    const response = await jobsApi.createJob(projectId, {
      mode,
      priorJobId,
      agentId,
    });
    // The create endpoint returns a slimmer shape than list — refetch to
    // keep the list consistent with the currentJob detail.
    try {
      const jobs = await jobsApi.listJobs(projectId);
      set({ jobs });
    } catch {
      // non-fatal — the poller will converge shortly
    }
    return response;
  },

  cancelJob: async (projectId, jobId) => {
    await jobsApi.cancelJob(projectId, jobId);
    set({
      jobs: get().jobs.map((j) =>
        (j.jobId || j.SK?.replace("JOB#", "")) === jobId
          ? { ...j, status: "cancelled" }
          : j,
      ),
      currentJob:
        get().currentJob?.jobId === jobId
          ? { ...get().currentJob, status: "cancelled" }
          : get().currentJob,
    });
  },

  deleteJob: async (projectId, jobId) => {
    await jobsApi.deleteJob(projectId, jobId);
    set({
      jobs: get().jobs.filter(
        (j) => (j.jobId || j.SK?.replace("JOB#", "")) !== jobId,
      ),
      currentJob:
        get().currentJob?.jobId === jobId ? null : get().currentJob,
    });
  },

  pollJobStatus: async (projectId, jobId) => {
    try {
      const job = await jobsApi.getJob(projectId, jobId);
      set({
        currentJob: job,
        jobs: get().jobs.map((j) => (j.jobId === jobId ? job : j)),
      });
      return job;
    } catch (err) {
      console.warn(`pollJobStatus failed: ${err.message}`);
      return null;
    }
  },

  clearCurrentJob: () => set({ currentJob: null }),

  // §15.3: Regenerate a single research output. The backend auto-resolves
  // a priorJobId from the most recent completed run. The store owns the
  // spinner-state set so the Documents tab and any reload can read it,
  // and owns the polling loop so individual DocumentRow components don't
  // have to.
  regenerateAgent: async (projectId, agentId) => {
    const already = get().regeneratingAgents.includes(agentId);
    if (already) return;
    set({
      regeneratingAgents: [...get().regeneratingAgents, agentId],
    });

    const stopSpinner = () =>
      set({
        regeneratingAgents: get().regeneratingAgents.filter(
          (a) => a !== agentId,
        ),
      });

    let job;
    try {
      job = await get().createJob(projectId, {
        mode: "single",
        agentId,
      });
    } catch (err) {
      stopSpinner();
      throw err;
    }

    // Poll until terminal, then refresh the document list so the
    // regenerated file's lastModified/size update in place.
    const jobId = job.jobId;
    const poll = async () => {
      try {
        const latest = await jobsApi.getJob(projectId, jobId);
        const terminal = !["pending", "running"].includes(latest.status);
        if (terminal) {
          stopSpinner();
          try {
            await get().loadDocuments(projectId);
          } catch {
            // non-fatal — the user can refresh manually
          }
          return;
        }
      } catch {
        // transient error — keep polling
      }
      setTimeout(poll, 5000);
    };
    setTimeout(poll, 3000);

    return job;
  },

  // ── Token usage (§7g / 7f backend) ───────────────────────────────────────
  tokenUsage: null, // { tokensUsedToday, dailyTokenBudget, tokenResetDate }

  loadUsage: async () => {
    try {
      const usage = await usageApi.getUsage();
      set({ tokenUsage: usage });
    } catch (err) {
      // Non-fatal — the budget footer just doesn't render if the
      // endpoint is unreachable. Logged for debugging.
      console.warn(`loadUsage failed: ${err.message}`);
    }
  },

  // ── Roundtable (§7h) ─────────────────────────────────────────────────────
  //
  // Two slices:
  //   1. `roundtablePanel` — the picker's selection state. An ordered
  //      array of personaIds, 2–3 entries, populated by the PanelPicker
  //      modal and consumed by the Roundtable page when the user kicks
  //      off the first turn.
  //   2. `activeRoundtable` — the in-flight turn state. Holds the live
  //      message list (including any panellist streaming right now),
  //      the current speaker index, panel metadata, and a flag for
  //      budget-exhaustion mid-turn. Cleared on true navigation away
  //      from the Roundtable page.

  roundtablePanel: [],

  addPanellist: (personaId) => {
    const current = get().roundtablePanel;
    if (current.length >= 3 || current.includes(personaId)) return;
    set({ roundtablePanel: [...current, personaId] });
  },

  removePanellist: (personaId) => {
    set({
      roundtablePanel: get().roundtablePanel.filter((id) => id !== personaId),
    });
  },

  togglePanellist: (personaId) => {
    // Deselect + re-select moves a persona to the end of the order.
    // Per PRD §4.3.3 ("panellists respond in the user-selected order"),
    // the reselect is effectively a fresh pick — future contributors
    // should not "fix" this to preserve the original position.
    const current = get().roundtablePanel;
    if (current.includes(personaId)) {
      set({ roundtablePanel: current.filter((id) => id !== personaId) });
    } else if (current.length < 3) {
      set({ roundtablePanel: [...current, personaId] });
    }
  },

  clearPanel: () => set({ roundtablePanel: [] }),

  // Active roundtable state shape:
  //   {
  //     conversationId: string | null,       // null during the initial
  //                                           // streaming window before
  //                                           // roundtable_started arrives
  //     personaIds: string[],                // full panel in speaking order
  //     messages: Array<{                    // interleaved user + assistant
  //       role: "user" | "assistant",
  //       personaId?: string,                // set for assistant messages
  //       content: string,                   // accumulated tokens
  //       streaming?: boolean,               // true while tokens arriving
  //       error?: string,                    // set on recoverable panel error
  //     }>,
  //     currentSpeakerIndex: number | null,  // -1 when none streaming
  //     isStreaming: boolean,
  //     budgetExhausted: boolean,
  //   }
  activeRoundtable: null,

  initActiveRoundtable: ({ conversationId, personaIds, userMessage }) =>
    set({
      activeRoundtable: {
        conversationId: conversationId || null,
        personaIds,
        messages: [{ role: "user", content: userMessage }],
        currentSpeakerIndex: null,
        isStreaming: true,
        budgetExhausted: false,
      },
    }),

  // Hydrate from a fetched conversation record (follow-ups, deep-links).
  // Symmetric with `hydrateActiveConversation` above — both take the
  // full conversation record so future stream surfaces (agent loop,
  // etc) can add a third hydrator with the same shape.
  hydrateActiveRoundtable: (conversation) => {
    set({
      activeRoundtable: {
        conversationId: conversation.id,
        personaIds: conversation.personaIds || [],
        messages: (conversation.messages || []).map((m) => ({
          role: m.role,
          personaId: m.personaId,
          content: m.content,
        })),
        currentSpeakerIndex: null,
        isStreaming: false,
        budgetExhausted: false,
      },
    });
  },

  // Called when the SSE stream's roundtable_started event arrives.
  // Fills in the conversationId so the page can navigate to the final URL.
  setRoundtableConversationId: (conversationId) => {
    const current = get().activeRoundtable;
    if (!current) return;
    set({ activeRoundtable: { ...current, conversationId } });
  },

  // Called on roundtable_speaker. Appends a new empty assistant message
  // for the incoming speaker and marks it as the current streaming slot.
  startRoundtableSpeaker: ({ personaId, index }) => {
    const current = get().activeRoundtable;
    if (!current) return;
    set({
      activeRoundtable: {
        ...current,
        currentSpeakerIndex: index,
        messages: [
          ...current.messages,
          { role: "assistant", personaId, content: "", streaming: true },
        ],
      },
    });
  },

  // Called on each attributed token event. Appends to the last message
  // only if it matches the personaId — defensive against out-of-order events.
  // The backend emits tokens strictly in per-speaker order (one
  // panellist streams to completion before the next starts), so a
  // mismatch here indicates a bug in the backend stream ordering or a
  // malformed event — log it so it's visible in dev consoles rather
  // than silently dropping tokens on the floor.
  appendRoundtableToken: (personaId, token) => {
    const current = get().activeRoundtable;
    if (!current || current.messages.length === 0) return;
    const messages = [...current.messages];
    const last = messages[messages.length - 1];
    if (last.role !== "assistant" || last.personaId !== personaId) {
      console.warn(
        `[roundtable] token for ${personaId} but current slot is ` +
          `${last.role === "assistant" ? last.personaId : last.role}; dropped`,
      );
      return;
    }
    messages[messages.length - 1] = {
      ...last,
      content: last.content + token,
    };
    set({ activeRoundtable: { ...current, messages } });
  },

  // Called on a recoverable panellist error (backend keeps the loop going).
  // Marks the currently-streaming message as failed with the error text.
  appendRoundtablePanelError: ({ personaId, error }) => {
    const current = get().activeRoundtable;
    if (!current || current.messages.length === 0) return;
    const messages = [...current.messages];
    const last = messages[messages.length - 1];
    if (
      last.role === "assistant" &&
      last.personaId === personaId &&
      last.streaming
    ) {
      messages[messages.length - 1] = {
        ...last,
        streaming: false,
        error,
      };
    } else {
      // No live slot for this persona — append a standalone error row.
      messages.push({
        role: "assistant",
        personaId,
        content: "",
        streaming: false,
        error,
      });
    }
    set({ activeRoundtable: { ...current, messages } });
  },

  // Called on roundtable_done. Marks the last streaming message as
  // complete and clears the streaming flag.
  finishRoundtableTurn: ({ budgetExhausted = false } = {}) => {
    const current = get().activeRoundtable;
    if (!current) return;
    const messages = current.messages.map((m) =>
      m.streaming ? { ...m, streaming: false } : m,
    );
    set({
      activeRoundtable: {
        ...current,
        messages,
        currentSpeakerIndex: null,
        isStreaming: false,
        budgetExhausted,
      },
    });
    get().loadUsage();
  },

  // Append a user message at the start of a follow-up turn.
  appendRoundtableUserMessage: (content) => {
    const current = get().activeRoundtable;
    if (!current) return;
    set({
      activeRoundtable: {
        ...current,
        messages: [...current.messages, { role: "user", content }],
        isStreaming: true,
        currentSpeakerIndex: null,
      },
    });
  },

  clearActiveRoundtable: () => set({ activeRoundtable: null }),

  // ── Toasts (§7g — global async feedback) ─────────────────────────────────
  toasts: [],

  pushToast: (message, variant = "info") => {
    const id = nextToastId++;
    set({ toasts: [...get().toasts, { id, message, variant }] });
    // Each toast gets its own independent timer set at push time, so a
    // burst of pushes (e.g. dropping 5 files, 2 fail) can't restart the
    // timers on earlier toasts — which was a bug with the previous
    // ToastContainer-owned useEffect approach.
    setTimeout(() => {
      set({ toasts: get().toasts.filter((t) => t.id !== id) });
    }, TOAST_TTL_MS);
    return id;
  },

  dismissToast: (id) => {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },

  // ── Active tenant (Phase 35.3 — super-admin tenant switcher) ─────────────
  // Null means "use the caller's own tenantId from the JWT claim". When set,
  // every API request includes X-Tenant-Id so super-admins can view any tenant.
  // Persisted to localStorage so the selection survives reloads — otherwise
  // a super-admin who switched to tenant_wiz on the admin page and then
  // reloaded would lose the header and POSTs would hit tenant-less routes.
  activeTenantId: loadPersistedActiveTenantId(),

  setActiveTenant: (tenantId) => {
    const prev = get().activeTenantId;
    const next = tenantId || null;
    set({ activeTenantId: next });
    persistActiveTenantId(next);
    // Propagate immediately into the api.js request helper.
    api.setActiveTenantHeader(next);

    // Reset every tenant-scoped slice that the previous tenant populated.
    // Phase 43.4 originally only cleared tenantStatus, but the store also
    // holds the previous tenant's projects/conversations/jobs/etc. and
    // each page's useEffect([]) only fires once per mount — without this
    // reset, switching tenants left stale data on screen until the user
    // hard-reloaded. Personas + roundtable panel + toasts are platform-
    // scoped (or ephemeral) and intentionally preserved.
    set({
      tenantStatus: null,
      tenantStatusFetchedAt: 0,
      tenantStatusError: null,
      conversations: [],
      conversationsError: null,
      activeMessages: [],
      isStreaming: false,
      projects: [],
      projectsError: null,
      currentProject: null,
      documents: [],
      documentsError: null,
      jobs: [],
      jobsError: null,
      currentJob: null,
      regeneratingAgents: [],
      tokenUsage: null,
      activeRoundtable: null,
    });

    // Hard-navigate to the same path on actual tenant change. Admin pages
    // (Prompts, Agents, Templates, Usage) keep their fetched data in
    // component-local useState with useEffect([]) deps, so a store reset
    // alone won't make them re-query. A full document reload is the only
    // consistent way to remount every route and let each useEffect
    // re-fire under the new X-Tenant-Id header. Reload to the current
    // pathname (rather than "/") so super-admins switching tenants while
    // on /admin/prompts stay on /admin/prompts and just see the new
    // tenant's data. Skip the reload on first-time selection (prev is
    // null) and on no-op re-selection (prev === next) to avoid loops.
    if (typeof window !== "undefined" && prev && prev !== next) {
      window.location.assign(window.location.pathname);
    }
  },
}));

// Rehydrate api.js module var on load so cross-route requests get the
// header without waiting for the TenantSwitcher (which mounts only on
// admin pages) to fire setActiveTenant.
api.setActiveTenantHeader(useStore.getState().activeTenantId);
